import type { Placement } from '@floating-ui/react-dom';
import type { ChangeEvent, KeyboardEvent, ReactNode } from 'react';
import { FloatingPortal } from '@floating-ui/react';
import { autoUpdate, flip, offset, useFloating } from '@floating-ui/react-dom';
import { useState } from 'react';

import { InputTitle, InputValue } from '.';
import { ButtonIcon, ButtonProps } from '../Button';
import { cn } from '../cn';
import { buttonHeightAndBorder } from '../common';
import { DefaultSelectOption } from '../common.type';
import { Arrows } from '../StrokeIcons';
import { InputProps } from './Input';
import SelectDropdownContainer from './SelectDropdownContainer';
import SelectDropdownNoResults from './SelectDropdownNoResults';
import SelectDropdownRow from './SelectDropdownRow';

export type SelectValueType = DefaultSelectOption & {
  desc?: string;
  icon?: React.JSX.Element;
  iconTooltip?: React.JSX.Element | string;
  labelJsx?: React.ReactNode;
  searchableLabel?: string;

};

export type SelectProps<OptionType = SelectValueType> = Omit<InputProps, 'rIcon' | 'onChange'> & {
  text?: ButtonProps['text'];
  placement?: Placement;
  options?: OptionType[];
  value?: string;
  optionRow?: (value: OptionType) => ReactNode;
  onChange?: (value: OptionType) => void;
  chevron?: 'down' | 'upDown';
};

const Select = ({
  size = 'base',
  icon,
  title,
  error,
  value,
  className,
  options,
  placement,
  optionRow,
  onChange,
  text,
  titleClassName,
  chevron,
  ref,
  ...props
}: SelectProps<SelectValueType>) => {
  const _valueOption = options?.find((o) => o.value === value || o.value === props.defaultValue);
  const [selectedOptionValue, setSelectedOptionValue] = useState<string | undefined>(_valueOption?.value);
  const [inputValue, setInputValue] = useState<string>(_valueOption?.label || '');
  const [highlightedOptionValue, setHighlightedOptionValue] = useState<string | undefined>(_valueOption?.value);
  const [isFocused, setIsFocused] = useState<boolean>(false);
  const { x, y, strategy, refs } = useFloating<HTMLLabelElement>({
    placement,
    middleware: [offset(4), flip()],
    whileElementsMounted: autoUpdate,
  });

  const selectOption = (el: SelectValueType) => {
    setInputValue(el.label);
    setSelectedOptionValue(el.value);
    setHighlightedOptionValue(el.value);
    if (onChange) {
      onChange(el);
    }
  };

  const onChangeInput = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const onBlur = () => {
    setIsFocused(false);
    const selectedOption = options?.find((o) => o.value === selectedOptionValue);
    setInputValue(selectedOption?.label ?? '');
    setHighlightedOptionValue(undefined);
  };

  let filteredOptions: SelectValueType[] | undefined;

  const selectedOption = options?.find((o) => o.value === selectedOptionValue);

  if (selectedOption?.label === inputValue) {
    filteredOptions = options || [];
  } else {
    filteredOptions = inputValue
      ? options?.filter((el) => el.label?.toLowerCase().includes(inputValue.toLowerCase()))
      : options;
  }

  const onKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      const currentHighlightIndex = filteredOptions?.findIndex((f) => f.value === highlightedOptionValue) ?? -1;
      const filteredOptionsLength = filteredOptions?.length ?? 0;
      const newIndex =
        currentHighlightIndex < 0 ? 0 : (currentHighlightIndex - 1 + filteredOptionsLength) % filteredOptionsLength;
      const valueToSet = filteredOptions?.[newIndex]?.value;
      setHighlightedOptionValue(valueToSet);
      refs.floating.current
        ?.querySelector(`[data-option-value="option-${valueToSet}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else if (e.key === 'ArrowDown') {
      const currentHighlightIndex = filteredOptions?.findIndex((f) => f.value === highlightedOptionValue) ?? -1;
      const filteredOptionsLength = filteredOptions?.length ?? 0;
      const newIndex = currentHighlightIndex < 0 ? 0 : (currentHighlightIndex + 1) % filteredOptionsLength;
      const valueToSet = filteredOptions?.[newIndex]?.value;
      setHighlightedOptionValue(valueToSet);
      refs.floating.current
        ?.querySelector(`[data-option-value="option-${valueToSet}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    } else if (e.key === 'Enter' && highlightedOptionValue) {
      const highlightedOption = options?.find((o) => o.value === highlightedOptionValue);
      if (highlightedOption) {
        e.currentTarget.blur();
        selectOption(highlightedOption);
      }
    }
  };

  return (
    <div
      className={cn(
        'inline-flex min-w-28 flex-col text-slate-600 dark:text-slate-100',
        {
          'gap-1': size === 'base',
          'min-w-22 gap-0.5': size === 'sm',
          'min-w-18 gap-0': size === 'xs',
        },
        className,
      )}>
      {title && (
        <InputTitle error={error} size={size} className={titleClassName}>
          {title}
        </InputTitle>
      )}
      <label
        ref={refs.setReference}
        className={cn(
          'inline-flex overflow-hidden font-sans transition-colors m-0',
          'bg-white focus-within:bg-apricot-white-50',
          'dark:border-charade-700 dark:bg-charade-950 dark:focus-within:bg-charade-800',
          {
            'border border-slate-200': !text,
            'border-fair-pink-300 dark:border-fair-pink-900': !text && error,
            'bg-fair-pink-100 text-fair-pink-600 dark:bg-fair-pink-950 dark:text-fair-pink-700 dark:focus-within:bg-fair-pink-900':
              error,
          },

          buttonHeightAndBorder(size),
        )}>
        {selectedOption?.icon == null && icon && <ButtonIcon size={size}>{icon}</ButtonIcon>}
        {selectedOption?.icon != null && <ButtonIcon size={size}>{selectedOption.icon}</ButtonIcon>}
        <InputValue
          {...props}
          value={inputValue}
          size={size}
          error={error}
          ref={ref}
          onFocus={() => setIsFocused(true)}
          onBlur={onBlur}
          onChange={onChangeInput}
          onKeyUp={onKeyUp}
        />

        <ButtonIcon size={size}>
          {!chevron && <Arrows.ChevronDown />}
          {chevron == 'down' && <Arrows.ChevronDown />}
          {chevron == 'upDown' && <Arrows.ChevronSelectorVertical />}
        </ButtonIcon>
      </label>

      <FloatingPortal>
        <SelectDropdownContainer
          x={x}
          y={y}
          setFloating={refs.setFloating}
          strategy={strategy}
          isOpen={isFocused}
          reference={refs.reference}
          size={size}>
          {filteredOptions?.length === 0 && <SelectDropdownNoResults size={size} />}
          {filteredOptions?.map((el) => (
            <SelectDropdownRow
              key={el.value}
              selectOption={selectOption}
              optionRow={optionRow}
              data={el}
              setHighlightedOptionValue={setHighlightedOptionValue}
              highlightedOptionValue={highlightedOptionValue}
              size={size}
            />
          ))}
        </SelectDropdownContainer>
      </FloatingPortal>
    </div>
  );
};

export default Select;
